import { useState, useEffect } from "react";
import { Page, useCurrent } from "./common";
import { PageHead, Modal, Meter } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, STUDIO } from "../lib/store";
import { useCan, useSession } from "../lib/auth";
import { storeImage, storeFile, storeFileSmart } from "../lib/sync";
import { pdfPageCount, extractPdfPageTexts, renderSheetPreview, scanDrawingSet, imageForScan } from "../lib/drawingScan";
import { QaModal, BoqModal } from "./DrawingAI";
import { SetDiffModal } from "./SetDiff";
import { IssueApprovalModal, ApprovalsPanel } from "./Approvals";
import { PartnersPanel } from "./Partners";
import { printTable } from "../lib/printView";
import { diffSheets } from "../lib/drawingDiff";
import { findDuplicateGroups, hashFile, type DupeGroup } from "../lib/drawingDupes";
import { prefetchSheets } from "../lib/pdfRender";
import { DrawingViewer, sheetKeyOf, type ViewerSheet } from "../components/DrawingViewer";
import { sendToStudio } from "../lib/bridge";
import { openWhatsApp } from "../lib/whatsapp";
import { fmtDate } from "../lib/format";
import { downloadTransmittalPdf } from "../lib/pdf";
import type { Project, Deliverable, DeliverablePhase, DrawingCloud, BoqItem } from "../lib/types";

const PHASE_ORDER: DeliverablePhase[] = ["Conceptual", "Design Development", "IFT"];
/* Disciplines are the "drawers" inside each building folder, in the usual set
   order. Anything unrecognised sorts to the end under its own name. */
const DISCIPLINE_ORDER = ["Architecture", "Interiors", "Structural", "MEP", "Civil", "Landscape", "Other"];
const discRank = (d: string) => { const i = DISCIPLINE_ORDER.indexOf(d); return i < 0 ? 99 : i; };
const DISC_HUE: Record<string, string> = { Architecture: "var(--accent)", Interiors: "var(--gold)", Structural: "var(--sage)", MEP: "var(--sky)", Civil: "var(--ink-3)", Landscape: "var(--sage)", Other: "var(--ink-3)" };
/** Deliverable → the viewer's sheet shape, and the register in reading order
 *  (phase, then sheet number) so prev/next walks the set naturally. */
function toViewerSheet(d: Deliverable): ViewerSheet {
  return { id: d.id, code: d.code, title: d.title, discipline: d.discipline, phase: d.phase, revision: d.revision, building: d.building, issuedOn: d.issuedOn, previewUrl: d.previewUrl, fileUrl: d.fileUrl, fileName: d.fileName, page: d.page, issues: d.issues, clouds: d.clouds };
}
function viewerRegistry(drawings: Deliverable[]): ViewerSheet[] {
  return [...drawings]
    .sort((a, b) => (PHASE_ORDER.indexOf(a.phase) - PHASE_ORDER.indexOf(b.phase)) || a.code.localeCompare(b.code))
    .map(toViewerSheet);
}
/** The sheets in the order they came in the uploaded file(s): grouped by source
 *  file, then by page. Lets the viewer page through a set exactly as submitted,
 *  before the register reorganised it by discipline/phase. */
function originalOrder(drawings: Deliverable[]): ViewerSheet[] {
  return [...drawings]
    .sort((a, b) =>
      (a.fileName || a.fileUrl || "~").localeCompare(b.fileName || b.fileUrl || "~") ||
      ((a.page ?? 1e9) - (b.page ?? 1e9)) ||
      a.code.localeCompare(b.code, undefined, { numeric: true }))
    .map(toViewerSheet);
}

const PHASES: DeliverablePhase[] = ["Conceptual", "Design Development", "IFT"];
const PHASE_LABEL: Record<DeliverablePhase, string> = { "Conceptual": "Conceptual", "Design Development": "Design Development", "IFT": "Issued for Tender / Construction" };
const PHASE_HUE: Record<DeliverablePhase, string> = { "Conceptual": "var(--sky)", "Design Development": "var(--sage)", "IFT": "var(--accent)" };

export default function Drawings() {
  const p = useCurrent();
  const canEdit = useCan("reviews");
  const session = useSession();
  const who = session?.name ?? STUDIO.principal;
  const [uploadOpen, setUploadOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [viewing, setViewing] = useState<Deliverable | null>(null);
  const [viewOrder, setViewOrder] = useState<"register" | "original">("register");
  const [renameOpen, setRenameOpen] = useState(false);
  const [qaOpen, setQaOpen] = useState(false);
  const [boqOpen, setBoqOpen] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [reviseFor, setReviseFor] = useState<Deliverable | null>(null);
  const [managing, setManaging] = useState<Deliverable | null>(null);
  const [q, setQ] = useState("");
  const [onlyDrawn, setOnlyDrawn] = useState(false);
  const [closed, setClosed] = useState<Set<string>>(new Set());  // collapsed discipline drawers
  const [shareOpen, setShareOpen] = useState(false);
  const [showPkgs, setShowPkgs] = useState(false);
  const [sort, setSort] = useState<"number" | "title" | "revision" | "updated">("number");
  const [selMode, setSelMode] = useState(false);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [dupeOpen, setDupeOpen] = useState(false);
  const [dupeScan, setDupeScan] = useState<{ done: number; total: number } | null>(null);
  const [dupeGroups, setDupeGroups] = useState<DupeGroup[] | null>(null);
  const [dupeDiag, setDupeDiag] = useState<{ compared: number; unreadable: number } | null>(null);
  const [sharePreselect, setSharePreselect] = useState<Deliverable[] | null>(null);
  // warm full-quality renders in the background so opening a sheet is instant
  useEffect(() => {
    const sheets = (p?.deliverables ?? []).filter((d) => d.fileUrl && !d.archived);
    if (sheets.length) prefetchSheets(sheets);
  }, [p?.id, p?.deliverables?.length]);
  if (!p) return null;
  const toggleSel = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = (ids: string[]) => ids.length > 0 && ids.every((id) => sel.has(id));
  const selectMany = (ids: string[], on: boolean) => setSel((s) => { const n = new Set(s); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; });
  const cmp = (a: Deliverable, b: Deliverable): number => {
    if (sort === "title") return a.title.localeCompare(b.title);
    if (sort === "revision") return (a.revision || "").localeCompare(b.revision || "") || a.code.localeCompare(b.code);
    if (sort === "updated") return (b.issuedOn || "").localeCompare(a.issuedOn || "") || a.code.localeCompare(b.code);
    return a.code.localeCompare(b.code, undefined, { numeric: true });   // number (natural)
  };

  const registry = (p.deliverables ?? []).filter((d) => (d.kind ?? "drawing") !== "document" || d.previewUrl || d.fileUrl);
  const archived = registry.filter((d) => d.archived);
  const all = registry.filter((d) => !d.archived);   // the working register excludes archived
  const withFiles = all.filter((d) => d.previewUrl || d.fileUrl).length;
  async function runDupeScan() {
    setDupeGroups(null); setDupeDiag(null); setDupeOpen(true); setDupeScan({ done: 0, total: 0 });
    const res = await findDuplicateGroups(all, (done, total) => setDupeScan({ done, total }));
    setDupeGroups(res.groups); setDupeDiag({ compared: res.compared, unreadable: res.unreadable }); setDupeScan(null);
  }
  const emptyCount = all.length - withFiles;
  // search across number, title, discipline, building and revision
  const query = q.trim().toLowerCase();
  const drawings = all.filter((d) => {
    if (onlyDrawn && !(d.previewUrl || d.fileUrl)) return false;
    if (!query) return true;
    return [d.code, d.title, d.discipline, d.building, d.revision].filter(Boolean).some((f) => f!.toLowerCase().includes(query));
  });
  const sets = p.drawingSets ?? [];
  // buildings on this project (explicit list ∪ any tagged on a sheet)
  const buildings = [...new Set([...(p.buildings ?? []), ...drawings.map((d) => d.building).filter(Boolean) as string[]])];
  const rawGroups: [string, Deliverable[]][] = buildings.length
    ? [...buildings.map((b) => [b, drawings.filter((d) => d.building === b)] as [string, Deliverable[]]),
       ...(drawings.some((d) => !d.building) ? [["", drawings.filter((d) => !d.building)] as [string, Deliverable[]]] : [])]
    : [["", drawings]];
  const buildingGroups = rawGroups.filter(([, list]) => list.length > 0);

  return (
    <Page>
      <PageHead kicker={`${p.name} · Construction drawings`} title="Drawings">
        {canEdit && <SyncButton p={p} />}
        {canEdit && drawings.length > 0 && <button className="btn ghost sm" onClick={() => setShareOpen(true)}><Icon.send width={15} />Share package</button>}
        {canEdit && drawings.length > 0 && <button className="btn ghost sm" onClick={() => setApproveOpen(true)}><Icon.sign width={15} />For approval</button>}
        {(p.drawingSets ?? []).length > 1 && <button className="btn ghost sm" title="Compare two set issues — added, withdrawn, revised" onClick={() => setDiffOpen(true)}><Icon.swap width={15} />Compare issues</button>}
        {canEdit && drawings.length > 0 && <button className="btn ghost sm" onClick={() => setIssueOpen(true)}><Icon.layers width={15} />Issue set revision</button>}
        {canEdit && <button className="btn accent sm" onClick={() => setUploadOpen(true)}><Icon.download width={15} style={{ transform: "rotate(180deg)" }} />Upload drawing set</button>}
      </PageHead>

      {canEdit && (p.packages?.length ?? 0) > 0 && (
        <div className="row" style={{ marginBottom: 12 }}>
          <button className="chip" onClick={() => setShowPkgs((s) => !s)}><Icon.send width={12} />{p.packages!.length} shared package{p.packages!.length === 1 ? "" : "s"}</button>
        </div>
      )}
      {showPkgs && canEdit && <PackagesPanel p={p} />}

      <p className="muted reveal" style={{ maxWidth: 680, marginBottom: 20 }}>
        The full drawing register, by phase and in full quality. Upload a drawing set and the studio mind reads it into the
        register — each sheet numbered, titled and filed. Revise a sheet to reissue it, or issue the whole set as a numbered
        revision the client and site can pull.
      </p>

      {registry.length === 0 ? (
        <div className="card pad reveal" style={{ maxWidth: 560 }}>
          <div className="kicker">No drawings yet</div>
          <p className="muted" style={{ fontSize: 14.5, marginTop: 8, lineHeight: 1.6 }}>
            Upload a drawing set (a PDF) and it's read into a full register — or generate the standard package from the
            Work Program and attach drawings sheet by sheet.
          </p>
          {canEdit && <button className="btn accent" style={{ marginTop: 14 }} onClick={() => setUploadOpen(true)}><Icon.download width={15} style={{ transform: "rotate(180deg)" }} />Upload a drawing set</button>}
        </div>
      ) : (
        <>
          <div className="row wrap reveal" style={{ gap: 8, marginBottom: 14 }}>
            <span className="badge"><Icon.layers width={11} />{query || onlyDrawn ? `${drawings.length} of ${all.length}` : all.length} sheets</span>
            <span className="badge">{withFiles} with drawings on file</span>
            {buildings.length > 0 && <span className="badge">{buildings.length} building{buildings.length === 1 ? "" : "s"}</span>}
            {sets.length > 0 && <span className="badge accent">{sets.length} set issue{sets.length === 1 ? "" : "s"}</span>}
          </div>

          {/* search + de-bloat */}
          <div className="row wrap reveal" style={{ gap: 8, marginBottom: 22, alignItems: "center" }}>
            <div className="row" style={{ gap: 8, flex: "1 1 280px", minWidth: 220, position: "relative" }}>
              <Icon.brief width={14} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--ink-3)", pointerEvents: "none" }} />
              <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number, title, discipline, building…" style={{ paddingLeft: 32, width: "100%" }} />
              {q && <button className="iconbtn" style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", width: 28, height: 28 }} onClick={() => setQ("")} title="Clear"><Icon.close width={13} /></button>}
            </div>
            <select className="select" style={{ width: "auto", fontSize: 12.5 }} value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} title="Sort sheets">
              <option value="number">Sort: number</option>
              <option value="title">Sort: title</option>
              <option value="revision">Sort: revision</option>
              <option value="updated">Sort: recently updated</option>
            </select>
            <button className={"chip" + (onlyDrawn ? " on" : "")} style={{ fontSize: 12, background: onlyDrawn ? "var(--accent)" : "var(--card)", color: onlyDrawn ? "#fff" : "var(--ink-2)", borderColor: onlyDrawn ? "var(--accent)" : "var(--line-2)" }} onClick={() => setOnlyDrawn((v) => !v)}>
              <Icon.check width={12} />With drawings only
            </button>
            <DownloadOriginals drawings={drawings} />
            {drawings.length > 0 && <button className="btn ghost sm" title="Print the drawing register" onClick={() => printTable({
              title: "Drawing register", subtitle: `${p.name} · ${p.code}`, meta: [`${drawings.length} sheets`, `${withFiles} with drawings on file`],
              columns: [{ key: "code", label: "Number", width: "80px" }, { key: "title", label: "Title" }, { key: "discipline", label: "Discipline", width: "90px" }, { key: "phase", label: "Phase", width: "90px" }, { key: "revision", label: "Rev", width: "45px", align: "center" }, { key: "building", label: "Building" }],
              rows: [...drawings].sort(cmp).map((d) => ({ code: d.code, title: d.title, discipline: d.discipline, phase: d.phase, revision: d.revision ?? "—", building: d.building ?? "" })),
            })}><Icon.report width={13} />Print</button>}
            {(() => { const ord = originalOrder(drawings.filter((d) => d.previewUrl || d.fileUrl)); return ord.length > 1 ? (
              <button className="btn ghost sm" title="Page through the sheets in the order they came in the uploaded file — as originally submitted, before the register reorganised them" onClick={() => { setViewOrder("original"); setViewing(drawings.find((d) => d.id === ord[0].id) ?? null); }}>
                <Icon.layers width={13} />Original order
              </button>
            ) : null; })()}
            {canEdit && withFiles > 0 && (
              <button className="btn ghost sm" title="Re-read each sheet's title block with AI and correct the number and title to match the actual drawing" onClick={() => setRenameOpen(true)}>
                <Icon.spark width={13} />Smart rename
              </button>
            )}
            {canEdit && all.length > 0 && (
              <button className="btn ghost sm" title="AI QA audit — flag gaps, undrawn sheets, numbering breaks and coordination issues before you issue" onClick={() => setQaOpen(true)}>
                <Icon.check width={13} />QA audit
              </button>
            )}
            {canEdit && withFiles > 0 && (
              <button className="btn ghost sm" title="AI take-off — read measurable quantities off the drawings into a bill of quantities" onClick={() => setBoqOpen(true)}>
                <Icon.program width={13} />Quantities
              </button>
            )}
            {canEdit && <button className={"chip" + (selMode ? " on" : "")} style={{ fontSize: 12, background: selMode ? "var(--accent)" : "var(--card)", color: selMode ? "#fff" : "var(--ink-2)", borderColor: selMode ? "var(--accent)" : "var(--line-2)" }} onClick={() => { setSelMode((v) => !v); setSel(new Set()); }}><Icon.check width={12} />{selMode ? "Done selecting" : "Select / edit"}</button>}
            {canEdit && emptyCount > 0 && (
              <button className="btn ghost sm" title="Purge the empty placeholder sheets left by the auto-generated register" onClick={() => { if (confirm(`Remove ${emptyCount} empty sheet${emptyCount === 1 ? "" : "s"} that have no drawing on file? Sheets with drawings are kept.`)) actions.removeEmptyDrawings(p.id); }}>
                <Icon.close width={13} />Remove {emptyCount} empty
              </button>
            )}
            {canEdit && withFiles > 1 && (
              <button className="btn ghost sm" title="Find sheets that are the same file — same name, size and content — in this or any building folder, and purge the copies" onClick={runDupeScan}>
                <Icon.copy width={13} />Find duplicates
              </button>
            )}
          </div>

          {/* bulk actions while selecting — a floating bar pinned to the bottom of
              the screen so it's always visible no matter how far you scroll into a
              folder (a top-sticky bar hid behind the app header). */}
          {selMode && (
            <div className="card row wrap" style={{ position: "fixed", left: "50%", bottom: 20, transform: "translateX(-50%)", zIndex: 50, padding: "10px 14px", gap: 12, alignItems: "center", justifyContent: "center", maxWidth: "min(940px, 94vw)", background: "var(--card)", border: "1px solid var(--line-2)", boxShadow: "var(--shadow-lg)" }}>
              <span className="row" style={{ gap: 10 }}>
                <b style={{ fontSize: 13.5 }}>{sel.size} selected</b>
                <button className="chip" style={{ fontSize: 11.5 }} onClick={() => setSel(new Set(drawings.map((d) => d.id)))}>All {drawings.length}</button>
                {sel.size > 0 && <button className="chip" style={{ fontSize: 11.5 }} onClick={() => setSel(new Set())}>Clear</button>}
              </span>
              <span className="row wrap" style={{ gap: 8 }}>
                <button className="btn ghost sm" disabled={!sel.size} title="Download the original files of the selected sheets" onClick={() => {
                  const uniq = [...new Map(all.filter((d) => sel.has(d.id) && d.fileUrl).map((d) => [d.fileUrl!, { url: d.fileUrl!, name: d.fileName || `${d.code}.pdf` }])).values()];
                  if (!uniq.length) { alert("None of the selected sheets have an original file on record."); return; }
                  uniq.forEach((f, i) => setTimeout(() => { const a = document.createElement("a"); a.href = f.url; a.download = f.name; document.body.appendChild(a); a.click(); a.remove(); }, i * 700));
                }}><Icon.download width={13} />Download</button>
                <button className="btn ghost sm" disabled={!sel.size} title="Share the selected sheets as a package" onClick={() => { setSharePreselect(all.filter((d) => sel.has(d.id))); setShareOpen(true); }}><Icon.arrowUpRight width={13} />Share</button>
                <button className="btn ghost sm" disabled={!sel.size} onClick={() => { actions.setDeliverablesArchived(p.id, [...sel], true); setSel(new Set()); }}><Icon.folder width={13} />Archive</button>
                <button className="btn accent sm" disabled={!sel.size} style={{ background: sel.size ? "var(--danger)" : undefined, borderColor: sel.size ? "var(--danger)" : undefined }} onClick={() => { if (confirm(`Permanently remove ${sel.size} sheet${sel.size === 1 ? "" : "s"} from the register? Archive instead if you might need them later.`)) { actions.removeDeliverables(p.id, [...sel]); setSel(new Set()); } }}><Icon.close width={13} />Delete{sel.size ? ` ${sel.size}` : ""}</button>
              </span>
              <button className="btn ghost sm" onClick={() => { setSelMode(false); setSel(new Set()); }}>Done</button>
            </div>
          )}

          {buildingGroups.length === 0 && (
            <div className="dim reveal" style={{ padding: "24px 4px", fontSize: 14 }}>
              {all.length === 0
                ? (archived.length > 0 ? "Every sheet is archived — restore one below to bring it back to the register." : "No active sheets. Upload a drawing set to populate the register.")
                : <>No sheets match “{q}”{onlyDrawn ? " with a drawing on file" : ""}.</>}
            </div>
          )}

          {/* Organizer: Building folder → Discipline drawer → sheets (by phase).
              Same-numbered sheets in different buildings stay apart. */}
          {buildingGroups.map(([bld, inBld]) => {
            const discs = [...new Set(inBld.map((d) => d.discipline))].sort((a, b) => discRank(a) - discRank(b) || a.localeCompare(b));
            return (
              <div key={bld || "_"} className="reveal" style={{ marginBottom: buildings.length ? 30 : 6 }}>
                {buildings.length > 0 && (
                  <div className="row between wrap" style={{ gap: 10, marginBottom: 14, paddingBottom: 8, borderBottom: "2px solid var(--line)" }}>
                    <h2 className="serif row" style={{ fontSize: 24, gap: 9, alignItems: "center" }}><Icon.folder width={19} style={{ color: "var(--ink-3)" }} />{bld || "Unassigned"}</h2>
                    <span className="row" style={{ gap: 10 }}>
                      {selMode && <button className="chip" style={{ fontSize: 11.5 }} onClick={() => { const ids = inBld.map((d) => d.id); selectMany(ids, !allSelected(ids)); }}>{allSelected(inBld.map((d) => d.id)) ? "Deselect folder" : `Select all ${inBld.length}`}</button>}
                      <span className="dim" style={{ fontSize: 12.5 }}>{inBld.length} sheet{inBld.length === 1 ? "" : "s"} · {discs.length} discipline{discs.length === 1 ? "" : "s"}</span>
                    </span>
                  </div>
                )}
                <div className="col" style={{ gap: 10 }}>
                  {discs.map((disc) => {
                    const inDisc = inBld.filter((d) => d.discipline === disc).sort((a, b) => (PHASE_ORDER.indexOf(a.phase) - PHASE_ORDER.indexOf(b.phase)) || cmp(a, b));
                    const key = `${bld}::${disc}`;
                    const open = !closed.has(key);
                    const drawn = inDisc.filter((d) => d.previewUrl || d.fileUrl).length;
                    return (
                      <div key={disc} className="card" style={{ overflow: "hidden" }}>
                        <button className="row between" onClick={() => setClosed((c) => { const n = new Set(c); n.has(key) ? n.delete(key) : n.add(key); return n; })}
                          style={{ width: "100%", padding: "13px 16px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left", gap: 10 }}>
                          <span className="row" style={{ gap: 11, alignItems: "center", minWidth: 0 }}>
                            <Icon.caretDown width={16} style={{ transform: open ? "none" : "rotate(-90deg)", transition: "transform .18s", color: "var(--ink-3)", flexShrink: 0 }} />
                            <span style={{ width: 9, height: 9, borderRadius: 3, background: DISC_HUE[disc] ?? "var(--ink-3)", flexShrink: 0 }} />
                            <span className="serif" style={{ fontSize: 18 }}>{disc}</span>
                          </span>
                          <span className="row" style={{ gap: 9, flexShrink: 0, alignItems: "center" }}>
                            {selMode && <span className="chip" role="button" tabIndex={0} style={{ fontSize: 10.5, cursor: "pointer" }}
                              onClick={(e) => { e.stopPropagation(); const ids = inDisc.map((d) => d.id); selectMany(ids, !allSelected(ids)); }}>
                              {allSelected(inDisc.map((d) => d.id)) ? "Deselect" : `Select all ${inDisc.length}`}
                            </span>}
                            <span className="dim" style={{ fontSize: 12 }}>{inDisc.length} sheet{inDisc.length === 1 ? "" : "s"}{drawn < inDisc.length ? ` · ${drawn} drawn` : ""}</span>
                          </span>
                        </button>
                        {open && (
                          <div style={{ padding: "0 16px 16px" }}>
                            {PHASES.map((ph) => {
                              const inPhase = inDisc.filter((d) => d.phase === ph);
                              if (!inPhase.length) return null;
                              return (
                                <div key={ph} style={{ marginTop: 8 }}>
                                  <div className="kicker" style={{ fontSize: 9, margin: "6px 0 8px", display: "flex", alignItems: "center", gap: 7 }}>
                                    <span style={{ width: 7, height: 7, borderRadius: 2, background: PHASE_HUE[ph] }} />{PHASE_LABEL[ph]} · {inPhase.length}
                                  </div>
                                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 13 }}>
                                    {inPhase.map((d) => (
                                      <SheetTile key={d.id} d={d} onView={() => selMode ? toggleSel(d.id) : (setViewOrder("register"), setViewing(d))} onRevise={canEdit ? () => setReviseFor(d) : undefined} onManage={canEdit ? () => setManaging(d) : undefined} selecting={selMode} selected={sel.has(d.id)} />
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <ApprovalsPanel p={p} />
          <PartnersPanel p={p} canEdit={canEdit} by={who} />

          {sets.length > 0 && (
            <div className="reveal" style={{ marginTop: 10 }}>
              <h3 className="serif" style={{ fontSize: 22, marginBottom: 14 }}>Set revisions</h3>
              <div className="col" style={{ gap: 10 }}>
                {sets.map((s) => (
                  <div key={s.id} className="card pad row between wrap" style={{ gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div className="row" style={{ gap: 8, alignItems: "baseline" }}>
                        <span style={{ fontWeight: 600, fontSize: 15 }}>{s.label}</span>
                        {s.sharedWithClient ? <span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />Shared</span> : <span className="badge" style={{ fontSize: 9 }}>Internal</span>}
                      </div>
                      <div className="dim" style={{ fontSize: 12, marginTop: 3 }}>{s.sheets.length} sheets · issued {fmtDate(s.issuedOn)}{s.by ? ` by ${s.by}` : ""}{s.note ? ` · ${s.note}` : ""}</div>
                    </div>
                    {canEdit && (
                      <button className={"btn sm " + (s.sharedWithClient ? "soft" : "ghost")} onClick={() => actions.shareDrawingSet(p.id, s.id, !s.sharedWithClient)}>
                        <Icon.arrowUpRight width={13} />{s.sharedWithClient ? "Shared with client" : "Share with client"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* archived sheets — hidden from the working register, recoverable */}
          {archived.length > 0 && (
            <div className="reveal" style={{ marginTop: 20 }}>
              <button className="row" style={{ gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: showArchived ? 12 : 0 }} onClick={() => setShowArchived((v) => !v)}>
                <Icon.caretDown width={15} style={{ transform: showArchived ? "none" : "rotate(-90deg)", transition: "transform .18s", color: "var(--ink-3)" }} />
                <Icon.folder width={15} style={{ color: "var(--ink-3)" }} />
                <span className="serif" style={{ fontSize: 18 }}>Archived</span>
                <span className="dim" style={{ fontSize: 12.5 }}>{archived.length}</span>
              </button>
              {showArchived && (
                <div className="card" style={{ overflow: "hidden" }}>
                  {archived.sort(cmp).map((d, i) => (
                    <div key={d.id} className="row between wrap" style={{ padding: "10px 14px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
                      <span className="row" style={{ gap: 10, minWidth: 0 }}>
                        <span className="mono dim" style={{ fontSize: 11.5 }}>{d.code} · {d.revision || "P1"}</span>
                        <span style={{ fontSize: 13, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.title}</span>
                        <span className="dim" style={{ fontSize: 11 }}>{d.discipline}{d.building ? ` · ${d.building}` : ""}</span>
                      </span>
                      {canEdit && (
                        <span className="row" style={{ gap: 6, flexShrink: 0 }}>
                          <button className="btn ghost sm" onClick={() => actions.setDeliverablesArchived(p.id, [d.id], false)}><Icon.swap width={13} />Restore</button>
                          <button className="iconbtn" style={{ width: 28, height: 28 }} title="Delete permanently" onClick={() => { if (confirm(`Permanently delete ${d.code}?`)) actions.removeDeliverables(p.id, [d.id]); }}><Icon.close width={13} /></button>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {uploadOpen && <UploadSetModal p={p} by={who} onClose={() => setUploadOpen(false)} />}
      {issueOpen && <IssueSetModal p={p} by={who} onClose={() => setIssueOpen(false)} />}
      {viewing && <DrawingViewer sheet={toViewerSheet(viewing)}
        registry={viewOrder === "original"
          ? originalOrder(all.filter((d) => d.previewUrl || d.fileUrl))
          : viewerRegistry(drawings.filter((d) => (d.building || undefined) === (viewing.building || undefined)))}
        onNavigate={(s) => setViewing(drawings.find((d) => d.id === s.id) ?? viewing)} onClose={() => setViewing(null)}
        notesApi={canEdit ? {
          sheetKey: sheetKeyOf, notes: p.drawingNotes ?? [], by: who, author: "studio",
          onAdd: (n) => actions.addDrawingNote(p.id, { ...n, author: "studio", by: who }),
          onReply: (id, body) => actions.replyDrawingNote(p.id, id, { author: "studio", by: who, body }),
          onResolve: (id, resolved) => actions.resolveDrawingNote(p.id, id, resolved),
        } : undefined} />}
      {shareOpen && <SharePackageModal p={p} drawings={drawings} preselect={sharePreselect} by={who} onClose={() => { setShareOpen(false); setSharePreselect(null); }} />}
      {reviseFor && <ReviseModal p={p} d={reviseFor} by={who} onClose={() => setReviseFor(null)} />}
      {managing && <ManageSheetModal p={p} d={managing} onClose={() => setManaging(null)} />}
      {dupeOpen && <PurgeDuplicatesModal p={p} scan={dupeScan} groups={dupeGroups} diag={dupeDiag} onRescan={runDupeScan} onClose={() => { setDupeOpen(false); setDupeGroups(null); setDupeScan(null); setDupeDiag(null); }} />}
      {renameOpen && <SmartRenameModal p={p} onClose={() => setRenameOpen(false)} />}
      {qaOpen && <QaModal p={p} sheets={all} by={who} onClose={() => setQaOpen(false)} />}
      {boqOpen && <BoqModal p={p} sheets={all} by={who} onClose={() => setBoqOpen(false)} />}
      {diffOpen && <SetDiffModal p={p} onClose={() => setDiffOpen(false)} />}
      {approveOpen && <IssueApprovalModal p={p} drawings={all} by={who} onClose={() => setApproveOpen(false)} />}
    </Page>
  );
}

/** One-click download of the original file(s) behind the current view. A set
 *  uploaded as one PDF shares a single original, so this is usually one file =
 *  the whole package; mixed uploads download each unique original in turn. */
function DownloadOriginals({ drawings }: { drawings: Deliverable[] }) {
  const uniq = [...new Map(drawings.filter((d) => d.fileUrl).map((d) => [d.fileUrl!, { url: d.fileUrl!, name: d.fileName || `${d.code}.pdf` }])).values()];
  if (uniq.length === 0) return null;
  function dl() {
    uniq.forEach((f, i) => setTimeout(() => {
      const a = document.createElement("a"); a.href = f.url; a.download = f.name;
      document.body.appendChild(a); a.click(); a.remove();
    }, i * 700));
  }
  return (
    <button className="btn ghost sm" onClick={dl}
      title={uniq.length === 1 ? `Download the original — ${uniq[0].name}` : `Download ${uniq.length} original files`}>
      <Icon.download width={13} />{uniq.length === 1 ? "Download original" : `Download originals (${uniq.length})`}
    </button>
  );
}

function SyncButton({ p }: { p: Project }) {
  const pm = p.links?.pm;
  return (
    <button className="btn ghost sm" title={pm ? `Re-sync drawings to ${pm.code || "Project Manager"}` : "Send to Project Manager"} onClick={() => sendToStudio(p, "pm")}>
      <Icon.swap width={14} />{pm ? "Sync → PM" : "Send to PM"}
    </button>
  );
}

function SheetTile({ d, onView, onRevise, onManage, selecting, selected }: { d: Deliverable; onView: () => void; onRevise?: () => void; onManage?: () => void; selecting?: boolean; selected?: boolean }) {
  return (
    <div className="card" style={{ overflow: "hidden", display: "flex", flexDirection: "column", outline: selected ? "2px solid var(--accent)" : "none", outlineOffset: -2 }}>
      <button onClick={onView} style={{ border: "none", padding: 0, background: "var(--paper-2)", cursor: "pointer", display: "block", aspectRatio: "1.35", overflow: "hidden", position: "relative" }}>
        {d.previewUrl
          ? <img src={d.previewUrl} alt={`${d.code} ${d.title}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: selecting && !selected ? 0.7 : 1 }} />
          : <span className="dim" style={{ display: "grid", placeItems: "center", height: "100%", fontSize: 12 }}>No preview</span>}
        <span className="badge" style={{ position: "absolute", top: 8, left: 8, background: "rgba(255,255,255,.92)", color: "var(--ink)", fontSize: 9 }}>{d.code} · {d.revision || "P1"}</span>
        {selecting && (
          <span style={{ position: "absolute", top: 8, right: 8, width: 22, height: 22, borderRadius: 6, display: "grid", placeItems: "center", background: selected ? "var(--accent)" : "rgba(255,255,255,.92)", border: "1.5px solid " + (selected ? "var(--accent)" : "var(--line-2)"), color: "#fff" }}>{selected && <Icon.check width={13} />}</span>
        )}
      </button>
      <div style={{ padding: "10px 12px", flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.35 }}>{d.title}</div>
        <div className="row between" style={{ marginTop: "auto" }}>
          <span className="dim" style={{ fontSize: 10.5 }}>{d.discipline}{(d.issues?.length ?? 0) > 1 ? ` · ${d.issues!.length} revs` : ""}</span>
          <span className="row" style={{ gap: 4 }}>
            {d.fileUrl && <a className="iconbtn" style={{ width: 26, height: 26 }} title="Download original" href={d.fileUrl} target="_blank" rel="noreferrer"><Icon.download width={13} /></a>}
            {onRevise && <button className="iconbtn" style={{ width: 26, height: 26 }} title="Reissue at next revision" onClick={onRevise}><Icon.swap width={13} /></button>}
            {onManage && <button className="iconbtn" style={{ width: 26, height: 26 }} title="Configure — edit, revisions, remove" onClick={onManage}><Icon.gear width={13} /></button>}
          </span>
        </div>
      </div>
    </div>
  );
}

/* One analysed page of an uploaded set, classified against the register. */
interface AnalyzedSheet {
  page: number; code: string; title: string; discipline: string; phase: DeliverablePhase; revision?: string;
  previewData?: string;
  status: "new" | "revision" | "duplicate";
  existingRev?: string;   // the current revision of the matched register sheet
}
interface UploadAnalysis {
  fileSize: number; fileHash?: string;
  wholeFileDup?: { count: number; when?: string; byHash: boolean };
  sheets: AnalyzedSheet[];
  bld?: string;
}

/* Upload a multi-page drawing set: render + read each page, let the AI name the
   sheets, then — before anything is filed — CLASSIFY each page against the
   register (new sheet / a revision of an existing one / a duplicate) and let the
   studio review and choose what to import. The original is stored once, on
   import. */
function UploadSetModal({ p, by, onClose }: { p: Project; by: string; onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState<{ label: string; pct: number; sub?: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [warn, setWarn] = useState<string | null>(null);
  const existingBuildings = [...new Set([...(p.buildings ?? []), ...((p.deliverables ?? []).map((d) => d.building).filter(Boolean) as string[])])];
  const [building, setBuilding] = useState("");
  const [newBuilding, setNewBuilding] = useState("");
  const [analysis, setAnalysis] = useState<UploadAnalysis | null>(null);
  const [skip, setSkip] = useState<Set<number>>(new Set());
  const mb = (b: number) => `${(b / 1048576).toFixed(1)} MB`;
  const bldOf = () => (building === "__new" ? newBuilding : building).trim() || undefined;

  // read + name + render + classify — no writes yet
  async function analyze() {
    if (!file) return;
    setBusy(true); setErr(null); setWarn(null); setAnalysis(null);
    try {
      setProg({ label: "Reading the file…", pct: 2 });
      const pages = await pdfPageCount(file);
      const total = Math.max(1, pages);
      if (total > 400) { setErr("That set has more than 400 pages — split it and upload in parts."); setBusy(false); setProg(null); return; }
      const fileSize = file.size;
      const fileHash = file.size <= 80 * 1024 * 1024 ? await hashFile(file) : undefined;
      const bld = bldOf();

      // whole-file duplicate — the exact same file already in the register?
      const dupSheets = (p.deliverables ?? []).filter((d) => (fileHash && d.fileHash === fileHash) || (!!d.fileName && d.fileName === file.name && d.fileSize === fileSize));
      const byHash = !!(fileHash && dupSheets.some((d) => d.fileHash === fileHash));
      const wholeFileDup = dupSheets.length ? { count: dupSheets.length, when: dupSheets.map((d) => d.issuedOn).filter(Boolean).sort()[0], byHash } : undefined;

      // 1) render every page first — the vision scan reads the title block off these
      const previews: (string | undefined)[] = [];
      for (let i = 0; i < total; i++) {
        setProg({ label: "Rendering previews", pct: 8 + Math.round((i / total) * 50), sub: `page ${i + 1} of ${total}` });
        previews.push((await renderSheetPreview(file, i + 1)) || undefined);
      }
      // 2) extracted text as a fallback hint
      const texts = pages > 0 ? await extractPdfPageTexts(file) : [""];
      // 3) SMART SCAN — the AI reads each sheet's TITLE BLOCK from the image
      const scanned = await scanDrawingSet(
        Array.from({ length: total }, (_, i) => ({ ref: `${file.name} p${i + 1}`, text: texts[i] || "", image: previews[i] })),
        (done, tot) => setProg({ label: "Reading title blocks", pct: 60 + Math.round((done / tot) * 38), sub: `${done} of ${tot}` }),
      );

      const sheets: AnalyzedSheet[] = [];
      for (let i = 0; i < total; i++) {
        const s = scanned?.[i];
        const code = s?.number || `SK-${String(i + 1).padStart(3, "0")}`;
        const existing = (p.deliverables ?? []).find((d) => d.code.trim().toUpperCase() === code.trim().toUpperCase() && (d.building || undefined) === (bld || undefined));
        const status: AnalyzedSheet["status"] = wholeFileDup ? "duplicate" : existing ? "revision" : "new";
        sheets.push({ page: i + 1, code, title: s?.title || `Sheet ${i + 1}`, discipline: s?.discipline || "Architecture", phase: (s?.phase as DeliverablePhase) || "IFT", revision: s?.revision || undefined, previewData: previews[i], status, existingRev: existing?.revision });
      }
      setSkip(new Set(sheets.filter((x) => x.status === "duplicate").map((x) => x.page)));   // pre-skip duplicates
      setAnalysis({ fileSize, fileHash, wholeFileDup, sheets, bld });
      setProg(null); setBusy(false);
    } catch (e: any) { setErr(e?.message || "Couldn't read that set."); setBusy(false); setProg(null); }
  }

  // store the original + previews and merge only the included sheets
  async function importSet() {
    if (!file || !analysis) return;
    setBusy(true); setErr(null); setWarn(null);
    try {
      const included = analysis.sheets.filter((s) => !skip.has(s.page));
      if (!included.length) { setErr("Every sheet is skipped — nothing to import."); setBusy(false); return; }
      let fileUrl: string | undefined; let fileName: string | undefined;
      try {
        const big = file.size > 80 * 1024 * 1024;
        const up = await storeFileSmart(file, (f) => setProg({ label: big ? "Uploading the original in parts" : "Storing the original", pct: Math.round(f * 68), sub: `${mb(file.size * f)} of ${mb(file.size)}` }));
        fileUrl = up.url; fileName = up.name;
      } catch (e: any) {
        setWarn(`The original file wasn't stored (${String(e?.message || "upload failed").slice(0, 90)}) — the register and previews are still being created.`);
      }
      const out: { code: string; title: string; discipline: string; phase: DeliverablePhase; revision?: string; previewUrl?: string; fileUrl?: string; fileName?: string; page?: number; fileSize?: number; fileHash?: string }[] = [];
      for (let i = 0; i < included.length; i++) {
        const s = included[i];
        setProg({ label: "Filing into the register", pct: 70 + Math.round((i / included.length) * 30), sub: `${i + 1} of ${included.length}` });
        let previewUrl: string | undefined;
        if (s.previewData) { try { previewUrl = await storeImage(s.previewData); } catch { previewUrl = s.previewData; } }
        out.push({ code: s.code, title: s.title, discipline: s.discipline, phase: s.phase, revision: s.revision, previewUrl, fileUrl, fileName, page: s.page, fileSize: analysis.fileSize, fileHash: analysis.fileHash });
      }
      actions.importDrawingSet(p.id, by, out, analysis.bld);
      setProg(null); setBusy(false); onClose();
    } catch (e: any) { setErr(e?.message || "Couldn't import that set."); setBusy(false); setProg(null); }
  }

  const counts = analysis && {
    new: analysis.sheets.filter((s) => s.status === "new" && !skip.has(s.page)).length,
    rev: analysis.sheets.filter((s) => s.status === "revision" && !skip.has(s.page)).length,
    dup: analysis.sheets.filter((s) => s.status === "duplicate").length,
    incl: analysis.sheets.filter((s) => !skip.has(s.page)).length,
  };
  const toggleSkip = (page: number) => setSkip((s) => { const n = new Set(s); n.has(page) ? n.delete(page) : n.add(page); return n; });
  const statusChip = (s: AnalyzedSheet) => s.status === "new"
    ? <span className="badge ok" style={{ fontSize: 8.5 }}>New</span>
    : s.status === "revision"
      ? <span className="badge accent" style={{ fontSize: 8.5 }}>Revision → {bumpRevLabel(s.existingRev || "P1")}</span>
      : <span className="badge" style={{ fontSize: 8.5, color: "var(--warn)" }}>Duplicate</span>;

  return (
    <Modal open onClose={busy ? () => {} : onClose} title="Upload a drawing set" wide={!!analysis}>
      <div className="col" style={{ gap: 14 }}>
        {!analysis ? (
          /* ---- pick the file + building, then analyze ---- */
          <>
            <p className="muted" style={{ fontSize: 14, lineHeight: 1.6 }}>
              Upload the drawing set as a <b>PDF</b>. Each page is read and named, then checked against the register — you'll see
              which pages are <b>new</b>, which are <b>revisions</b> of existing sheets, and whether the whole file is a <b>duplicate</b>, before anything is filed.
            </p>
            <label className="panel row between" style={{ padding: "12px 14px", borderStyle: "dashed", gap: 12, cursor: busy ? "default" : "pointer" }}>
              <span className="row" style={{ gap: 10, minWidth: 0 }}>
                <Icon.download width={17} style={{ color: "var(--accent-ink)", flexShrink: 0 }} />
                <span style={{ fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{file?.name || "Choose a PDF drawing set"}</span>
              </span>
              <span className="btn soft sm">Browse</span>
              <input type="file" accept="application/pdf,.pdf" hidden disabled={busy} onChange={(e) => { setFile(e.target.files?.[0] ?? null); setErr(null); }} />
            </label>

            <div className="field">
              <label>Building / drawing area <span className="dim">— for multi-building schemes</span></label>
              <select className="select" value={building} disabled={busy} onChange={(e) => setBuilding(e.target.value)}>
                <option value="">Whole project (no separate building)</option>
                {existingBuildings.map((b) => <option key={b} value={b}>{b}</option>)}
                <option value="__new">+ New building / area…</option>
              </select>
              {building === "__new" && <input className="input" style={{ marginTop: 8 }} value={newBuilding} disabled={busy} onChange={(e) => setNewBuilding(e.target.value)} placeholder="e.g. Main Sanctuary, Fellowship Hall" autoFocus />}
            </div>

            {prog && (
              <div className="col" style={{ gap: 6 }}>
                <div className="row between" style={{ fontSize: 12.5 }}>
                  <span className="row" style={{ gap: 7, color: "var(--accent-ink)" }}><Icon.spark width={14} />{prog.label}{prog.sub ? <span className="dim"> · {prog.sub}</span> : null}</span>
                  <span className="mono dim" style={{ fontSize: 12 }}>{prog.pct}%</span>
                </div>
                <Meter value={prog.pct} />
              </div>
            )}
            {err && <div style={{ color: "var(--danger)", fontSize: 12.5 }}>{err}</div>}
            <div className="dim" style={{ fontSize: 11.5 }}>Reading a full set renders every page — a large set can take a few minutes. The bar shows each stage.</div>
            <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
              <button className="btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
              <button className="btn accent" disabled={!file || busy} onClick={analyze}><Icon.spark width={15} />{busy ? "Reading…" : "Analyze & review"}</button>
            </div>
          </>
        ) : (
          /* ---- review what will be filed ---- */
          <>
            {analysis.wholeFileDup && (
              <div className="panel" style={{ padding: "12px 14px", borderLeft: "3px solid var(--warn)", background: "color-mix(in srgb, var(--warn) 8%, var(--card))" }}>
                <div className="row" style={{ gap: 8, marginBottom: 4 }}><Icon.copy width={15} style={{ color: "var(--warn)" }} /><b style={{ fontSize: 13.5 }}>This looks like a duplicate upload</b></div>
                <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.5 }}>{analysis.wholeFileDup.byHash ? "The exact same file" : "A file with the same name and size"} is already in the register ({analysis.wholeFileDup.count} sheet{analysis.wholeFileDup.count === 1 ? "" : "s"}{analysis.wholeFileDup.when ? `, filed ${fmtDate(analysis.wholeFileDup.when)}` : ""}). Every page is set to <b>skip</b> — turn pages on below only if you mean to re-file them (e.g. into a different building).</p>
              </div>
            )}

            <div className="row between wrap" style={{ gap: 10 }}>
              <span className="row wrap" style={{ gap: 6 }}>
                {!!counts!.new && <span className="badge ok" style={{ fontSize: 10 }}>{counts!.new} new</span>}
                {!!counts!.rev && <span className="badge accent" style={{ fontSize: 10 }}>{counts!.rev} revision{counts!.rev === 1 ? "" : "s"}</span>}
                {!!counts!.dup && <span className="badge" style={{ fontSize: 10, color: "var(--warn)" }}>{counts!.dup} duplicate{counts!.dup === 1 ? "" : "s"}</span>}
                {analysis.bld && <span className="badge" style={{ fontSize: 10 }}><Icon.folder width={9} />{analysis.bld}</span>}
              </span>
              <span className="row" style={{ gap: 6 }}>
                <button className="chip" style={{ fontSize: 11 }} onClick={() => setSkip(new Set())}>Include all</button>
                <button className="chip" style={{ fontSize: 11 }} onClick={() => setSkip(new Set(analysis.sheets.filter((s) => s.status === "duplicate").map((s) => s.page)))}>Skip duplicates</button>
              </span>
            </div>

            <div className="col" style={{ gap: 2, maxHeight: "46vh", overflowY: "auto", border: "1px solid var(--line)", borderRadius: 10, padding: 6 }}>
              {analysis.sheets.map((s) => {
                const inc = !skip.has(s.page);
                return (
                  <label key={s.page} className="row" style={{ gap: 10, padding: "6px 8px", borderRadius: 8, cursor: "pointer", opacity: inc ? 1 : 0.5, background: inc ? "transparent" : "var(--paper-2)" }}>
                    <input type="checkbox" checked={inc} onChange={() => toggleSkip(s.page)} />
                    {s.previewData
                      ? <img src={s.previewData} alt="" style={{ width: 46, height: 33, objectFit: "cover", borderRadius: 3, border: "1px solid var(--line)", flexShrink: 0, background: "#fff" }} />
                      : <span style={{ width: 46, height: 33, borderRadius: 3, border: "1px solid var(--line)", flexShrink: 0, display: "grid", placeItems: "center", fontSize: 8, color: "var(--ink-3)" }}>p{s.page}</span>}
                    <span className="row" style={{ gap: 8, minWidth: 0, flex: 1 }}>
                      <span className="mono dim" style={{ fontSize: 11.5, flexShrink: 0 }}>{s.code}</span>
                      <span style={{ fontSize: 12.5, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.title}</span>
                    </span>
                    {statusChip(s)}
                  </label>
                );
              })}
            </div>

            {prog && (
              <div className="col" style={{ gap: 6 }}>
                <div className="row between" style={{ fontSize: 12.5 }}>
                  <span className="row" style={{ gap: 7, color: "var(--accent-ink)" }}><Icon.spark width={14} />{prog.label}{prog.sub ? <span className="dim"> · {prog.sub}</span> : null}</span>
                  <span className="mono dim" style={{ fontSize: 12 }}>{prog.pct}%</span>
                </div>
                <Meter value={prog.pct} />
              </div>
            )}
            {warn && <div style={{ color: "var(--warn)", fontSize: 12.5 }}>{warn}</div>}
            {err && <div style={{ color: "var(--danger)", fontSize: 12.5 }}>{err}</div>}

            <div className="row between" style={{ gap: 10 }}>
              <button className="btn ghost sm" disabled={busy} onClick={() => { setAnalysis(null); setProg(null); }}><Icon.back width={13} />Back</button>
              <div className="row" style={{ gap: 10 }}>
                <button className="btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
                <button className="btn accent" disabled={busy || !counts!.incl} onClick={importSet}><Icon.check width={15} />{busy ? "Filing…" : `Import ${counts!.incl} sheet${counts!.incl === 1 ? "" : "s"}`}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/* Reissue one sheet: upload the revised drawing, note what changed. */
function ReviseModal({ p, d, by, onClose }: { p: Project; d: Deliverable; by: string; onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // smart scan: render the new sheet, diff it against the current revision
  const [prep, setPrep] = useState<{ previewData?: string; clouds?: DrawingCloud[]; scanning: boolean } | null>(null);
  const nextRev = bumpRevLabel(d.revision || "P1");

  useEffect(() => {
    if (!file) { setPrep(null); return; }
    let alive = true;
    setPrep({ scanning: true });
    (async () => {
      const previewData = (await renderSheetPreview(file, 1)) || undefined;
      if (!alive) return;
      let clouds: DrawingCloud[] | undefined;
      if (previewData && d.previewUrl) { try { clouds = await diffSheets(d.previewUrl, previewData); } catch { /* best-effort */ } }
      if (!alive) return;
      setPrep({ previewData, clouds, scanning: false });
    })();
    return () => { alive = false; };
  }, [file, d.previewUrl]);

  async function run() {
    setBusy(true); setErr(null);
    try {
      let fileUrl: string | undefined, fileName: string | undefined, previewUrl: string | undefined, page: number | undefined;
      const clouds = prep?.clouds;
      if (file) {
        try { const up = await storeFile(file); fileUrl = up.url; fileName = up.name; } catch { /* storage off */ }
        const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
        page = isPdf ? 1 : undefined;
        const previewData = prep?.previewData ?? ((await renderSheetPreview(file, 1)) || undefined);
        if (previewData) { try { previewUrl = await storeImage(previewData); } catch { previewUrl = previewData; } }
      }
      actions.reviseDeliverable(p.id, d.id, by, { fileUrl, fileName, previewUrl, page, note, clouds });
      setBusy(false); onClose();
    } catch (e: any) { setErr(e?.message || "Couldn't reissue that."); setBusy(false); }
  }

  const clouds = prep?.clouds ?? [];
  return (
    <Modal open onClose={busy ? () => {} : onClose} title={`Reissue ${d.code} — ${nextRev}`}>
      <div className="col" style={{ gap: 14 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>Reissues <b>{d.code}</b> from {d.revision || "P1"} to <b>{nextRev}</b>. Upload the revised drawing (PDF or image) and note what changed. The previous issue is kept, superseded.</p>
        <label className="panel row between" style={{ padding: "12px 14px", borderStyle: "dashed", gap: 12, cursor: "pointer" }}>
          <span style={{ fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{file?.name || "Choose the revised drawing"}</span>
          <span className="btn soft sm">Browse</span>
          <input type="file" accept="application/pdf,.pdf,image/*" hidden onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>

        {/* smart-scan result: a thumbnail of the new sheet with change regions boxed */}
        {file && (
          <div className="panel" style={{ padding: 12 }}>
            <div className="row between" style={{ marginBottom: prep?.previewData ? 10 : 0 }}>
              <span className="kicker" style={{ fontSize: 9 }}>Smart change scan</span>
              {prep?.scanning
                ? <span className="badge" style={{ fontSize: 10 }}><span className="dot" />Scanning vs {d.revision || "P1"}…</span>
                : !d.previewUrl
                  ? <span className="dim" style={{ fontSize: 11 }}>No prior render to compare</span>
                  : <span className={"badge " + (clouds.length ? "accent" : "ok")} style={{ fontSize: 10 }}>{clouds.length ? `${clouds.length} change${clouds.length === 1 ? "" : "s"} found` : "No changes detected"}</span>}
            </div>
            {prep?.previewData && (
              <div style={{ position: "relative", width: "100%", aspectRatio: "1100 / 780", background: "#fff", borderRadius: 6, overflow: "hidden", border: "1px solid var(--line)" }}>
                <img src={prep.previewData} alt="revised sheet" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
                {clouds.map((c, i) => (
                  <div key={i} style={{ position: "absolute", left: `${c.x * 100}%`, top: `${c.y * 100}%`, width: `${c.w * 100}%`, height: `${c.h * 100}%`, border: "2px solid #d4341f", borderRadius: 8, boxShadow: "0 0 0 2px rgba(212,52,31,.15)" }} />
                ))}
              </div>
            )}
            {clouds.length > 0 && <p className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.5 }}>These regions changed vs {d.revision || "P1"} — they'll be marked with revision clouds on {nextRev} in the viewer.</p>}
          </div>
        )}

        <div className="field"><label>What changed</label>
          <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Grid revised to suit structural coordination; door schedule updated." />
        </div>
        {err && <div style={{ color: "var(--danger)", fontSize: 12.5 }}>{err}</div>}
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={busy || (!file && !note.trim())} onClick={run}><Icon.check width={15} />{busy ? "Issuing…" : `Issue ${nextRev}`}</button>
        </div>
      </div>
    </Modal>
  );
}

/* Configure a sheet: edit its register details, prune old revisions, remove it. */
function ManageSheetModal({ p, d, onClose }: { p: Project; d: Deliverable; onClose: () => void }) {
  const [code, setCode] = useState(d.code);
  const [title, setTitle] = useState(d.title);
  const [discipline, setDiscipline] = useState(d.discipline);
  const [phase, setPhase] = useState<DeliverablePhase>(d.phase);
  const [building, setBuilding] = useState(d.building ?? "");
  const [revision, setRevision] = useState(d.revision ?? "P1");
  const buildings = [...new Set([...(p.buildings ?? []), ...((p.deliverables ?? []).map((x) => x.building).filter(Boolean) as string[])])];
  // the live sheet, so the revision list reflects removals without reopening
  const live = (p.deliverables ?? []).find((x) => x.id === d.id) ?? d;
  const issues = live.issues ?? [];

  function save() {
    actions.editDeliverable(p.id, d.id, { code, title, discipline, phase, building, revision });
    onClose();
  }
  return (
    <Modal open onClose={onClose} title={`Configure ${d.code}`} wide>
      <div className="col" style={{ gap: 14 }}>
        <div className="row" style={{ gap: 10 }}>
          <div className="field" style={{ width: 130 }}><label>Sheet number</label><input className="input mono" value={code} onChange={(e) => setCode(e.target.value)} /></div>
          <div className="field" style={{ flex: 1 }}><label>Title</label><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div className="field" style={{ width: 90 }}><label>Revision</label><input className="input mono" value={revision} onChange={(e) => setRevision(e.target.value)} /></div>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <div className="field" style={{ flex: 1 }}><label>Discipline</label>
            <select className="select" value={discipline} onChange={(e) => setDiscipline(e.target.value)}>
              {["Architecture", "Structural", "MEP", "Interiors", "Landscape", "Civil", "Other"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}><label>Phase</label>
            <select className="select" value={phase} onChange={(e) => setPhase(e.target.value as DeliverablePhase)}>
              {PHASES.map((x) => <option key={x} value={x}>{PHASE_LABEL[x]}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}><label>Building / area</label>
            <select className="select" value={buildings.includes(building) ? building : (building ? "__keep" : "")} onChange={(e) => setBuilding(e.target.value === "__keep" ? building : e.target.value)}>
              <option value="">— none —</option>
              {buildings.map((b) => <option key={b} value={b}>{b}</option>)}
              {building && !buildings.includes(building) && <option value="__keep">{building}</option>}
            </select>
          </div>
        </div>

        {/* revision history — prune superseded issues */}
        {issues.length > 0 && (
          <div>
            <div className="kicker" style={{ fontSize: 9, marginBottom: 8 }}>Revision history</div>
            <div className="col" style={{ gap: 6 }}>
              {[...issues].reverse().map((r, i) => {
                const isCurrent = i === 0;
                return (
                  <div key={r.rev + r.issuedOn} className="panel row between" style={{ padding: "8px 12px", gap: 10 }}>
                    <span className="row" style={{ gap: 10, minWidth: 0 }}>
                      <span className="badge accent mono" style={{ fontSize: 10 }}>{r.rev}</span>
                      {isCurrent && <span className="badge ok" style={{ fontSize: 9 }}>Current</span>}
                      <span className="dim" style={{ fontSize: 12 }}>{fmtDate(r.issuedOn)}{r.note ? ` · ${r.note}` : ""}{r.fileUrl ? "" : " · no file"}</span>
                    </span>
                    {!isCurrent && (
                      <button className="iconbtn" style={{ width: 26, height: 26 }} title="Remove this old revision" onClick={() => { if (confirm(`Remove superseded revision ${r.rev}?`)) actions.removeDrawingIssue(p.id, d.id, r.rev); }}><Icon.close width={13} /></button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>The current revision can't be removed — reissue it or delete the sheet.</div>
          </div>
        )}

        <div className="row between wrap" style={{ marginTop: 4, paddingTop: 12, borderTop: "1px solid var(--line)", gap: 10 }}>
          <div className="row" style={{ gap: 8 }}>
            {live.archived
              ? <button className="btn ghost sm" onClick={() => { actions.setDeliverablesArchived(p.id, [d.id], false); onClose(); }}><Icon.swap width={14} />Restore</button>
              : <button className="btn ghost sm" title="Hide from the working register — recoverable" onClick={() => { actions.setDeliverablesArchived(p.id, [d.id], true); onClose(); }}><Icon.folder width={14} />Archive</button>}
            <button className="btn ghost sm" style={{ color: "var(--danger)", borderColor: "color-mix(in srgb, var(--danger) 40%, var(--line))" }}
              onClick={() => { if (confirm(`Delete sheet ${d.code} — ${d.title}? This removes it from the register (archive instead to keep it).`)) { actions.removeDeliverable(p.id, d.id); onClose(); } }}>
              <Icon.close width={14} />Delete
            </button>
          </div>
          <div className="row" style={{ gap: 10 }}>
            <button className="btn ghost" onClick={onClose}>Cancel</button>
            <button className="btn accent" onClick={save}><Icon.check width={15} />Save changes</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* Freeze the whole register as a numbered issue — the overall revision. */
function IssueSetModal({ p, by, onClose }: { p: Project; by: string; onClose: () => void }) {
  const n = (p.drawingSets?.length ?? 0) + 1;
  const [label, setLabel] = useState(`IFT — Issue ${String.fromCharCode(64 + n)}`);
  const [note, setNote] = useState("");
  const [share, setShare] = useState(true);
  const count = (p.deliverables ?? []).filter((d) => (d.kind ?? "drawing") !== "document" || d.previewUrl || d.fileUrl).length;
  return (
    <Modal open onClose={onClose} title="Issue a set revision">
      <div className="col" style={{ gap: 14 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>Freezes all <b>{count}</b> sheets at their current revision as one numbered issue — a package the client and site can always pull, even after later sheets change.</p>
        <div className="field"><label>Issue name</label><input className="input" autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="IFT — Issue C" /></div>
        <div className="field"><label>Note <span className="dim">(optional)</span></label><input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Issued for construction." /></div>
        <label className="row" style={{ gap: 8, fontSize: 13.5, cursor: "pointer" }}>
          <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} />Share this issue with the client
        </label>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={!label.trim()} onClick={() => { actions.issueDrawingSetSnapshot(p.id, { label, note, shareWithClient: share }, by); onClose(); }}>
            <Icon.layers width={15} />Issue {label.trim() || "set"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function bumpRevLabel(rev: string): string {
  const m = /^([A-Za-z]*)(\d+)$/.exec(rev || "P1");
  return m ? `${m[1]}${parseInt(m[2], 10) + 1}` : "P2";
}

/* ---- share a project package (tokened link) --------------------------- */
function SharePackageModal({ p, drawings, preselect, by, onClose }: { p: Project; drawings: Deliverable[]; preselect?: Deliverable[] | null; by: string; onClose: () => void }) {
  const hasPre = !!(preselect && preselect.length);
  // smart-detect the recipient from the party the audience points to
  const recipientFor = (a: "client" | "consultant" | "contractor") => {
    if (a === "client") return { name: p.client.name || "", email: p.client.email || "", phone: p.client.phone || "" };
    if (a === "consultant") { const c = (p.consultants ?? []).find((x) => x.status === "appointed") ?? (p.consultants ?? [])[0]; return { name: c?.contact || c?.firm || "", email: c?.email || "", phone: c?.phone || "" }; }
    return { name: "", email: "", phone: "" };   // contractor — no record on file
  };
  const [audience, setAudience] = useState<"client" | "consultant" | "contractor">("client");
  const [scope, setScope] = useState<"selected" | "current" | "discipline">(hasPre ? "selected" : "current");
  const [pickDisc, setPickDisc] = useState<string[]>([]);
  const seed0 = recipientFor("client");
  const [name, setName] = useState(seed0.name);
  const [email, setEmail] = useState(seed0.email);
  const [phone, setPhone] = useState(seed0.phone);
  const [note, setNote] = useState("");
  const [title, setTitle] = useState(`${p.name} — drawings`);
  const [share, setShare] = useState<{ ref: string; token: string } | null>(null);
  const [copied, setCopied] = useState(false);
  // re-fill the recipient whenever the audience changes (no retyping the email)
  useEffect(() => { const r = recipientFor(audience); setName(r.name); setEmail(r.email); setPhone(r.phone); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [audience]);
  const allDisc = [...new Set(drawings.map((d) => d.discipline))];
  const withFile = (d: Deliverable) => !!(d.fileUrl || d.previewUrl);
  const docs = (scope === "selected" ? (preselect ?? []) : scope === "current" ? drawings : drawings.filter((d) => pickDisc.includes(d.discipline))).filter(withFile);

  function create() {
    if (!docs.length) return;
    const s = actions.createPackage(p.id, {
      title: title.trim() || `${p.name} — drawings`, audience,
      recipientName: name.trim() || undefined, recipientEmail: email.trim() || undefined,
      deliverableIds: docs.map((d) => d.id), note: note.trim() || undefined,
    }, by);
    setShare({ ref: s.ref, token: s.token });
  }
  const link = share ? `https://${STUDIO.domain}/pkg/${share.token}` : "";
  const shareMsg = share ? `${title.trim() || `${p.name} — drawings`}\n\nYou can view and download the drawings here:\n${link}` : "";
  const mailtoHref = share
    ? `mailto:${email || ""}?subject=${encodeURIComponent(`${title.trim() || `${p.name} — drawings`} (${share.ref})`)}&body=${encodeURIComponent(`Hi${name ? " " + name.split(" ")[0] : ""},\n\n${note ? note.trim() + "\n\n" : ""}You can view and download the drawings here:\n${link}\n\nBest regards,\n${by}\n${STUDIO.name}`)}`
    : "";

  return (
    <Modal open onClose={onClose} title="Share a drawing package" wide>
      {share ? (
        <div className="col" style={{ gap: 14 }}>
          <div className="panel" style={{ padding: 14, borderLeft: "3px solid var(--accent)" }}>
            <div className="row" style={{ gap: 8, marginBottom: 6 }}><Icon.check width={16} style={{ color: "var(--accent-ink)" }} /><b>{share.ref} shared</b></div>
            <p className="dim" style={{ fontSize: 12.5, marginBottom: 10 }}>{email ? `The studio emailed the link to ${email}. ` : ""}Anyone with this link can open and download{audience === "client" ? " and leave revision comments" : ""}.</p>
            <div className="row" style={{ gap: 8 }}>
              <input className="input mono" readOnly value={link} style={{ flex: 1, fontSize: 12 }} onFocus={(e) => e.currentTarget.select()} />
              <button className="btn accent sm" onClick={() => { void navigator.clipboard?.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? "Copied ✓" : "Copy"}</button>
            </div>
            {/* share the link yourself — from your own email or WhatsApp */}
            <div className="row wrap" style={{ gap: 8, marginTop: 10 }}>
              <a className="btn soft sm" href={mailtoHref}><Icon.send width={13} />Email from my address</a>
              <button className="btn" style={{ background: "#25D366", borderColor: "#25D366", color: "#fff" }} onClick={() => { const ph = phone.trim(); if (audience === "client" && ph && ph !== (p.client.phone ?? "")) actions.updateClient(p.id, { phone: ph }); openWhatsApp(shareMsg, ph || undefined); }}><Icon.whatsapp width={15} />{phone.trim() ? "Send on WhatsApp" : "Share on WhatsApp"}</button>
            </div>
            <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>WhatsApp {phone.trim() ? `opens ${audience === "client" ? p.client.name.split(" ")[0] : "the recipient"}'s chat` : "lets you pick the contact"} with the drawing-set link ready to send. “Email from my address” sends from your own mail app{email ? ` to ${email}` : ""}.</div>
          </div>
          <div className="row" style={{ justifyContent: "flex-end" }}><button className="btn" onClick={onClose}>Done</button></div>
        </div>
      ) : (
        <div className="col" style={{ gap: 14 }}>
          <div className="field"><label>Send to</label>
            <div className="row wrap" style={{ gap: 6 }}>
              {(["client", "consultant", "contractor"] as const).map((a) => (
                <button key={a} className={"chip" + (audience === a ? " on" : "")} style={{ textTransform: "capitalize" }} onClick={() => setAudience(a)}>{a}</button>
              ))}
            </div>
            <p className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>{audience === "client" ? "The client — a review set; they can leave revision comments." : audience === "consultant" ? "A sub-consultant (structural / MEP) — for coordination." : "The contractor — the issued set for pricing or site."}</p>
          </div>
          <div className="field"><label>Which sheets</label>
            <div className="row wrap" style={{ gap: 6, marginBottom: 8 }}>
              {hasPre && <button className={"chip" + (scope === "selected" ? " on" : "")} onClick={() => setScope("selected")}>Selected ({preselect!.length})</button>}
              <button className={"chip" + (scope === "current" ? " on" : "")} onClick={() => setScope("current")}>Current view</button>
              <button className={"chip" + (scope === "discipline" ? " on" : "")} onClick={() => setScope("discipline")}>By discipline</button>
            </div>
            {scope === "discipline" && (
              <div className="row wrap" style={{ gap: 6 }}>
                {allDisc.map((d) => <button key={d} className={"chip" + (pickDisc.includes(d) ? " on" : "")} onClick={() => setPickDisc((x) => x.includes(d) ? x.filter((y) => y !== d) : [...x, d])}>{d}</button>)}
              </div>
            )}
            <p className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>{docs.length} sheet{docs.length === 1 ? "" : "s"} with a drawing on file{docs.length === 0 ? " — none match" : ""}</p>
          </div>
          <div className="field"><label>Package title</label><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div className="row between" style={{ marginBottom: -4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-2)" }}>Recipient</label>
            {(name || email || phone) && <span className="badge" style={{ fontSize: 9 }}><Icon.spark width={9} />auto-filled from the {audience}</span>}
          </div>
          <div className="row" style={{ gap: 12 }}>
            <div className="field" style={{ flex: 1 }}><label>Name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={audience === "client" ? p.client.name : "Name"} /></div>
            <div className="field" style={{ flex: 1.3 }}><label>Email — sends the link</label><input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@…" /></div>
          </div>
          <div className="field"><label>WhatsApp number <span className="dim">— for the WhatsApp share</span></label><input className="input mono" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+592 600 0000" /></div>
          <div className="field"><label>Covering note (optional)</label><textarea className="input" value={note} onChange={(e) => setNote(e.target.value)} style={{ minHeight: 64 }} placeholder={audience === "client" ? "For your review — please note any revisions." : "Issued for coordination."} /></div>
          <div className="row between">
            <button className="btn ghost" onClick={onClose}>Cancel</button>
            <button className="btn accent" onClick={create} disabled={docs.length === 0}><Icon.send width={15} />Create &amp; share {docs.length} sheet{docs.length === 1 ? "" : "s"}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ---- shared packages + client comment review -------------------------- */
function PackagesPanel({ p }: { p: Project }) {
  const shares = p.packages ?? [];
  return (
    <div className="card" style={{ padding: 18, marginBottom: 18 }}>
      <div className="kicker" style={{ marginBottom: 12 }}>Shared packages</div>
      <div className="col" style={{ gap: 12 }}>
        {shares.map((s) => {
          const open = (s.clientNotes ?? []).filter((n) => !n.resolved).length;
          const link = `https://${STUDIO.domain}/pkg/${s.token}`;
          return (
            <div key={s.id} className="panel" style={{ padding: "11px 13px" }}>
              <div className="row between wrap" style={{ gap: 8 }}>
                <div className="row" style={{ gap: 8, minWidth: 0 }}>
                  <span className="mono dim" style={{ fontSize: 11 }}>{s.ref}</span>
                  <span style={{ fontWeight: 500, fontSize: 13.5 }}>{s.title}</span>
                  <span className="badge" style={{ fontSize: 9.5, textTransform: "capitalize" }}>{s.audience}</span>
                </div>
                <div className="row" style={{ gap: 6 }}>
                  <button className="iconbtn" style={{ width: 26, height: 26 }} title="Copy link" onClick={() => void navigator.clipboard?.writeText(link)}><Icon.link width={12} /></button>
                  <button className="iconbtn" style={{ width: 26, height: 26 }} title="Download transmittal (PDF record of this issue)" onClick={() => void downloadTransmittalPdf(p, s)}><Icon.download width={13} /></button>
                  <a className="iconbtn" style={{ width: 26, height: 26 }} title="Open package" target="_blank" rel="noreferrer" href={link}><Icon.arrowUpRight width={13} /></a>
                  <button className="iconbtn" style={{ width: 26, height: 26 }} title="Remove" onClick={() => actions.removePackage(p.id, s.id)}><Icon.close width={12} /></button>
                </div>
              </div>
              <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>{s.deliverableIds.length} sheet{s.deliverableIds.length === 1 ? "" : "s"} · {s.recipientName ?? "—"} · {fmtDate(s.sharedAt)}{open > 0 ? ` · ${open} open comment${open === 1 ? "" : "s"}` : ""}</div>
              {s.receipt && <div className="row" style={{ gap: 5, alignItems: "center", marginTop: 5, color: "var(--ok, #2e7d32)", fontSize: 11.5 }}><Icon.check width={12} />Receipt confirmed by {s.receipt.by} · {fmtDate(s.receipt.at)}</div>}
              {(s.clientNotes ?? []).length > 0 && (
                <div className="col" style={{ gap: 6, marginTop: 8 }}>
                  {(s.clientNotes ?? []).map((n) => (
                    <div key={n.id} className="row between" style={{ gap: 8, background: "var(--card)", borderRadius: 8, padding: "7px 10px", opacity: n.resolved ? 0.55 : 1 }}>
                      <span style={{ fontSize: 12.5 }}>{n.code && <span className="badge mono" style={{ fontSize: 9, marginRight: 5 }}>{n.code}</span>}{n.text}<span className="dim" style={{ fontSize: 10.5 }}> — {n.by}</span></span>
                      <button className="iconbtn" style={{ width: 22, height: 22 }} title={n.resolved ? "Reopen" : "Mark addressed"} onClick={() => actions.resolvePackageNote(p.id, s.id, n.id, !n.resolved)}><Icon.check width={11} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Find & purge duplicate sheets — same file title, size and content, in this or
   any building folder. One copy of each is kept; the user picks which of the
   rest to remove. */
function PurgeDuplicatesModal({ p, scan, groups, diag, onRescan, onClose }: {
  p: Project; scan: { done: number; total: number } | null; groups: DupeGroup[] | null; diag: { compared: number; unreadable: number } | null; onRescan: () => void; onClose: () => void;
}) {
  const [remove, setRemove] = useState<Set<string>>(new Set());
  const [applied, setApplied] = useState(false);
  useEffect(() => {
    if (!groups) return;
    const def = new Set<string>();
    groups.forEach((g) => g.items.forEach((it) => { if (!it.keep) def.add(it.id); }));
    setRemove(def);
  }, [groups]);

  const keptIn = (g: DupeGroup) => g.items.filter((x) => !remove.has(x.id)).length;
  const toggle = (id: string) => setRemove((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const totalDupes = groups ? groups.reduce((s, g) => s + g.items.length - 1, 0) : 0;

  function purge() {
    const ids = [...remove];
    if (!ids.length) return;
    if (!confirm(`Permanently remove ${ids.length} duplicate sheet${ids.length === 1 ? "" : "s"}? The kept copy of each stays in the register.`)) return;
    actions.removeDeliverables(p.id, ids);
    setApplied(true);
    setTimeout(onClose, 700);
  }

  return (
    <Modal open onClose={onClose} title="Find & purge duplicates" wide>
      <div className="col" style={{ gap: 14 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>
          Matched by <b>file name, size and content</b> (a checksum of the actual file) — a true duplicate whether the copies sit in the same building folder or different ones. One copy of each set is kept.
        </p>

        {scan ? (
          <div className="panel" style={{ padding: 16 }}>
            <div className="row" style={{ gap: 10, marginBottom: scan.total > 0 ? 10 : 0 }}><span className="dot" /><b style={{ fontSize: 13.5 }}>Scanning the register…</b></div>
            {scan.total > 0 && <><Meter value={(scan.done / scan.total) * 100} /><div className="dim" style={{ fontSize: 12, marginTop: 6 }}>Comparing sheet {scan.done} of {scan.total}</div></>}
          </div>
        ) : !groups ? (
          <div className="dim" style={{ padding: 20, textAlign: "center" }}>Preparing…</div>
        ) : groups.length === 0 ? (
          <div className="panel" style={{ padding: 22, textAlign: "center" }}>
            <Icon.check width={22} style={{ color: "var(--ok)" }} />
            <div style={{ fontWeight: 600, marginTop: 6 }}>No duplicates found</div>
            <div className="dim" style={{ fontSize: 12.5, marginTop: 4 }}>
              {diag ? `Compared ${diag.compared} sheet${diag.compared === 1 ? "" : "s"} by number, title and rendered content${diag.unreadable ? ` · ${diag.unreadable} preview${diag.unreadable === 1 ? "" : "s"} couldn't be read` : ""}.` : "Every sheet with a drawing on file is unique."}
              {diag && diag.unreadable > 0 ? " Copies whose previews didn't load can only be matched by sheet number + title." : ""}
            </div>
          </div>
        ) : (
          <>
            <div className="row between wrap" style={{ gap: 10 }}>
              <span className="badge accent">{groups.length} duplicate set{groups.length === 1 ? "" : "s"} · {totalDupes} extra cop{totalDupes === 1 ? "y" : "ies"}</span>
              <span className="row" style={{ gap: 8 }}>
                <button className="chip" style={{ fontSize: 11.5 }} onClick={() => { const s = new Set<string>(); groups.forEach((g) => g.items.forEach((it) => { if (!it.keep) s.add(it.id); })); setRemove(s); }}>Select all extras</button>
                <button className="chip" style={{ fontSize: 11.5 }} onClick={() => setRemove(new Set())}>Clear</button>
                <span className="dim" style={{ fontSize: 12 }}>{remove.size} to remove</span>
              </span>
            </div>
            <div className="col" style={{ gap: 12, maxHeight: "46vh", overflowY: "auto" }}>
              {groups.map((g) => (
                <div key={g.key} className="panel" style={{ padding: 12 }}>
                  <div className="row between" style={{ marginBottom: 8, gap: 10 }}>
                    <span className="row" style={{ gap: 8, minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.label}</span>
                      <span className={"badge " + (g.confidence === "content" ? "ok" : "")} style={{ fontSize: 8.5, flexShrink: 0 }} title={g.confidence === "content" ? "Same sheet number and identical rendered content" : "Same sheet number and title (content not verified — check the previews)"}>{g.confidence === "content" ? "identical content" : "same number & title"}</span>
                    </span>
                    <span className="dim" style={{ fontSize: 11.5, flexShrink: 0 }}>{g.items.length} copies · {new Set(g.items.map((i) => i.building || "Unassigned")).size} folder{new Set(g.items.map((i) => i.building || "Unassigned")).size === 1 ? "" : "s"}</span>
                  </div>
                  <div className="col" style={{ gap: 2 }}>
                    {g.items.map((it) => {
                      const marked = remove.has(it.id);
                      const lockKeep = !marked && keptIn(g) <= 1;   // never let the last copy be removed
                      return (
                        <label key={it.id} className="row between" style={{ padding: "6px 8px", borderRadius: 8, gap: 10, cursor: lockKeep ? "default" : "pointer", background: marked ? "color-mix(in srgb, var(--danger) 10%, transparent)" : "transparent" }}>
                          <span className="row" style={{ gap: 9, minWidth: 0 }}>
                            <input type="checkbox" checked={marked} disabled={lockKeep} onChange={() => toggle(it.id)} />
                            <span className="mono dim" style={{ fontSize: 11.5 }}>{it.code}</span>
                            <span style={{ fontSize: 12.5, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</span>
                            {it.building && <span className="badge" style={{ fontSize: 8.5 }}><Icon.folder width={8} />{it.building}</span>}
                            {it.revisions > 0 && <span className="dim" style={{ fontSize: 10.5 }}>{it.revisions} rev</span>}
                            {it.archived && <span className="badge" style={{ fontSize: 8.5 }}>archived</span>}
                          </span>
                          {marked
                            ? <span className="badge" style={{ fontSize: 9, color: "var(--danger)", flexShrink: 0 }}>Remove</span>
                            : <span className="badge ok" style={{ fontSize: 9, flexShrink: 0 }}><Icon.check width={9} />Keep</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="row between" style={{ marginTop: 4 }}>
          <button className="btn ghost sm" onClick={onRescan} disabled={!!scan}><Icon.swap width={13} />Re-scan</button>
          <div className="row" style={{ gap: 10 }}>
            <button className="btn ghost" onClick={onClose}>{applied ? "Close" : "Cancel"}</button>
            {groups && groups.length > 0 && <button className="btn accent" disabled={!remove.size || applied} onClick={purge}><Icon.close width={14} />{applied ? "Purged" : `Purge ${remove.size} duplicate${remove.size === 1 ? "" : "s"}`}</button>}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* Smart rename — re-read each sheet's TITLE BLOCK with AI (vision) and correct
   the number + title to match the actual drawing. Fixes a register whose sheets
   were mis-named on an earlier scan. */
interface RenameRow { id: string; oldCode: string; oldTitle: string; code: string; title: string; discipline: string; phase: DeliverablePhase; changed: boolean }
function SmartRenameModal({ p, onClose }: { p: Project; onClose: () => void }) {
  const sheets = (p.deliverables ?? []).filter((d) => (d.previewUrl || d.fileUrl) && !d.archived);
  const [prog, setProg] = useState<{ label: string; done: number; total: number } | null>({ label: "Loading sheets", done: 0, total: sheets.length });
  const [rows, setRows] = useState<RenameRow[] | null>(null);
  const [apply, setApply] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // 1) load each sheet's preview image (downscaled) for the vision scan
        const pages: { ref: string; text: string; image?: string }[] = [];
        for (let i = 0; i < sheets.length; i++) {
          const d = sheets[i];
          const image = d.previewUrl ? (await imageForScan(d.previewUrl)) || undefined : undefined;
          pages.push({ ref: `${d.code} ${d.title}`, text: `current: ${d.code} — ${d.title} (${d.discipline})`, image });
          if (alive) setProg({ label: "Loading sheets", done: i + 1, total: sheets.length });
        }
        if (!alive) return;
        // 2) vision scan the title blocks
        const res = await scanDrawingSet(pages, (done, total) => alive && setProg({ label: "Reading title blocks", done, total }));
        if (!alive) return;
        if (!res) { setErr("Couldn't read the title blocks — the AI scanner may be unavailable (needs ANTHROPIC_API_KEY)."); setProg(null); return; }
        const out: RenameRow[] = res.map((s, i) => {
          const d = sheets[i];
          const code = (s?.number || "").trim() || d.code;
          const title = (s?.title || "").trim() || d.title;
          const discipline = s?.discipline || d.discipline;
          const phase = ((s?.phase as DeliverablePhase) || d.phase);
          const changed = code !== d.code || title !== d.title || discipline !== d.discipline || phase !== d.phase;
          return { id: d.id, oldCode: d.code, oldTitle: d.title, code, title, discipline, phase, changed };
        });
        setRows(out);
        setApply(new Set(out.filter((r) => r.changed).map((r) => r.id)));
        setProg(null);
      } catch (e: any) { if (alive) { setErr(e?.message || "Scan failed."); setProg(null); } }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id: string) => setApply((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const changedRows = (rows ?? []).filter((r) => r.changed);

  function run() {
    if (!rows) return;
    setBusy(true);
    rows.filter((r) => apply.has(r.id)).forEach((r) => actions.editDeliverable(p.id, r.id, { code: r.code, title: r.title, discipline: r.discipline, phase: r.phase }));
    setBusy(false); onClose();
  }

  return (
    <Modal open onClose={busy ? () => {} : onClose} title="Smart rename from the drawings" wide>
      <div className="col" style={{ gap: 14 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>
          The AI re-reads each sheet's <b>title block</b> and proposes the drawing number and title exactly as printed — fixing any that were mis-named. Review the changes and apply the ones you want.
        </p>

        {prog ? (
          <div className="panel" style={{ padding: 16 }}>
            <div className="row" style={{ gap: 10, marginBottom: 10 }}><span className="dot" /><b style={{ fontSize: 13.5 }}>{prog.label}…</b></div>
            <Meter value={prog.total ? (prog.done / prog.total) * 100 : 0} />
            <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>{prog.done} of {prog.total}</div>
          </div>
        ) : err ? (
          <div style={{ color: "var(--danger)", fontSize: 13 }}>{err}</div>
        ) : !rows ? (
          <div className="dim" style={{ padding: 20, textAlign: "center" }}>Preparing…</div>
        ) : changedRows.length === 0 ? (
          <div className="panel" style={{ padding: 22, textAlign: "center" }}>
            <Icon.check width={22} style={{ color: "var(--ok)" }} />
            <div style={{ fontWeight: 600, marginTop: 6 }}>Every sheet already matches its title block</div>
            <div className="dim" style={{ fontSize: 12.5, marginTop: 4 }}>Scanned {rows.length} sheet{rows.length === 1 ? "" : "s"} — no changes needed.</div>
          </div>
        ) : (
          <>
            <div className="row between wrap" style={{ gap: 10 }}>
              <span className="badge accent">{changedRows.length} of {rows.length} sheet{rows.length === 1 ? "" : "s"} would change</span>
              <span className="row" style={{ gap: 8 }}>
                <button className="chip" style={{ fontSize: 11.5 }} onClick={() => setApply(new Set(changedRows.map((r) => r.id)))}>Select all</button>
                <button className="chip" style={{ fontSize: 11.5 }} onClick={() => setApply(new Set())}>Clear</button>
                <span className="dim" style={{ fontSize: 12 }}>{apply.size} to apply</span>
              </span>
            </div>
            <div className="col" style={{ gap: 2, maxHeight: "46vh", overflowY: "auto", border: "1px solid var(--line)", borderRadius: 10, padding: 6 }}>
              {changedRows.map((r) => (
                <label key={r.id} className="row" style={{ gap: 10, padding: "8px 10px", borderRadius: 8, cursor: "pointer", background: apply.has(r.id) ? "color-mix(in srgb, var(--accent) 7%, transparent)" : "transparent" }}>
                  <input type="checkbox" checked={apply.has(r.id)} onChange={() => toggle(r.id)} style={{ marginTop: 3 }} />
                  <span className="col" style={{ gap: 2, minWidth: 0, flex: 1 }}>
                    <span className="row" style={{ gap: 8, minWidth: 0, flexWrap: "wrap" }}>
                      <span className="mono dim" style={{ fontSize: 11.5, textDecoration: r.code !== r.oldCode ? "line-through" : "none" }}>{r.oldCode}</span>
                      {r.code !== r.oldCode && <><Icon.arrowUpRight width={11} style={{ color: "var(--accent-ink)" }} /><span className="mono" style={{ fontSize: 11.5, fontWeight: 700, color: "var(--accent-ink)" }}>{r.code}</span></>}
                    </span>
                    <span className="row" style={{ gap: 8, minWidth: 0, flexWrap: "wrap" }}>
                      <span className="dim" style={{ fontSize: 12, textDecoration: r.title !== r.oldTitle ? "line-through" : "none" }}>{r.oldTitle}</span>
                      {r.title !== r.oldTitle && <><Icon.arrowUpRight width={10} style={{ color: "var(--accent-ink)" }} /><span style={{ fontSize: 12.5, fontWeight: 600 }}>{r.title}</span></>}
                    </span>
                  </span>
                  <span className="dim" style={{ fontSize: 10.5, flexShrink: 0 }}>{r.discipline}</span>
                </label>
              ))}
            </div>
          </>
        )}

        <div className="row between">
          <span className="dim" style={{ fontSize: 11 }}>{rows ? `${rows.length} scanned` : ""}</span>
          <div className="row" style={{ gap: 10 }}>
            <button className="btn ghost" disabled={busy} onClick={onClose}>{rows && changedRows.length === 0 ? "Close" : "Cancel"}</button>
            {rows && changedRows.length > 0 && <button className="btn accent" disabled={busy || !apply.size} onClick={run}><Icon.check width={15} />Apply {apply.size} rename{apply.size === 1 ? "" : "s"}</button>}
          </div>
        </div>
      </div>
    </Modal>
  );
}
